import { useMemo } from "react";
import { Outlet } from "react-router-dom";
import { MainLayout } from "./MainLayout";
import type { MenuItem } from "./Sidebar";
import {
  FaUsers,
  FaChartLine,
  FaListAlt,
  FaUserPlus,
  FaUserShield,
  FaShieldAlt,
  FaTrophy,
  FaFutbol,
  FaCrown,
  FaGift,
} from "react-icons/fa";
import { UsersPermissionsGuardEnum, RolesGuardEnum, AdminGuardEnum } from "@/core/enums";
import { useAuthStore } from "@/frontend/stores";

// Configuración centralizada del menú
const allMenuItems: MenuItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <FaChartLine />,
    path: "/dashboard",
  },
  {
    key: "users",
    label: "Usuarios",
    icon: <FaUsers />,
    children: [
      {
        key: "list-users",
        label: "Listado de Usuarios",
        icon: <FaListAlt />,
        path: "/users",
        permissions: [UsersPermissionsGuardEnum.USERS_VIEW],
      },
      {
        key: "create-user",
        label: "Crear Usuario",
        icon: <FaUserPlus />,
        path: "/users/create",
        permissions: [UsersPermissionsGuardEnum.USERS_CREATE],
      },
    ],
  },
  {
    key: "roles",
    label: "Roles",
    icon: <FaUserShield />,
    children: [
      {
        key: "list-roles",
        label: "Listado de Roles",
        icon: <FaListAlt />,
        path: "/roles",
        permissions: [RolesGuardEnum.ROLES_VIEW],
      },
      {
        key: "create-role",
        label: "Crear Rol",
        icon: <FaShieldAlt />,
        path: "/roles/create",
        permissions: [RolesGuardEnum.ROLES_CREATE],
      },
    ],
  },
  {
    key: "tournaments",
    label: "Torneos",
    icon: <FaTrophy />,
    permissions: [AdminGuardEnum.ADMIN_ACCESS],
    children: [
      {
        key: "list-tournaments",
        label: "Listado de Torneos",
        icon: <FaListAlt />,
        path: "/tournaments",
      },
      {
        key: "list-disciplines",
        label: "Disciplinas",
        icon: <FaListAlt />,
        path: "/disciplines",
      },
      {
        key: "list-tournament-formats",
        label: "Eventos de Torneos",
        icon: <FaListAlt />,
        path: "/tournament-formats",
      },
      {
        key: "list-sub-categories",
        label: "Subcategorías",
        icon: <FaListAlt />,
        path: "/sub-categories",
      },
      {
        key: "list-match-teams",
        label: "Equipos",
        icon: <FaListAlt />,
        path: "/match-teams",
      },
      {
        key: "list-matches",
        label: "Partidos",
        icon: <FaFutbol />,
        path: "/matches",
      },
      // tournament-format-templates: oculto del navbar (en desarrollo)
      // { key: "list-tournament-format-templates", label: "Plantillas de Formatos", icon: <FaListAlt />, path: "/tournament-format-templates" },
    ],
  },
  {
    key: "sports-predictions",
    label: "Predicciones deportivas",
    icon: <FaFutbol />,
    children: [
      {
        key: "my-pools",
        label: "Mis grupos",
        icon: <FaCrown />,
        path: "/sports-predictions/pools/my-pools",
      },
      {
        key: "list-prize-types",
        label: "Tipos de premiación",
        icon: <FaGift />,
        path: "/prize-types",
        permissions: [AdminGuardEnum.ADMIN_ACCESS],
      },
    ],
  },
];

/**
 * Filtra los items del menú basándose en los permisos del usuario
 */
function filterMenuItemsByPermissions(
  items: MenuItem[],
  hasPermission: (guardSlug: string) => boolean,
  hasAnyPermission: (guardSlugs: string[]) => boolean,
): MenuItem[] {
  return items
    .map((item) => {
      // Si el item tiene hijos, filtrar los hijos primero
      if (item.children) {
        const filteredChildren = filterMenuItemsByPermissions(
          item.children,
          hasPermission,
          hasAnyPermission,
        );

        // Si no quedan hijos visibles, no mostrar el item padre
        if (filteredChildren.length === 0) {
          return null;
        }

        // Si el item padre tiene permisos propios, validarlos
        if (item.permissions && item.permissions.length > 0) {
          if (!hasAnyPermission(item.permissions)) {
            return null;
          }
        }

        return {
          ...item,
          children: filteredChildren,
        };
      }

      // Si el item no tiene hijos pero tiene permisos, validarlos
      if (item.permissions && item.permissions.length > 0) {
        if (!hasAnyPermission(item.permissions)) {
          return null;
        }
      }

      // Si no tiene permisos definidos o tiene los permisos necesarios, mostrarlo
      return item;
    })
    .filter((item): item is MenuItem => item !== null);
}

/**
 * Layout protegido que filtra el menú basándose en permisos
 * NOTA: Este componente solo filtra el menú para mostrar/ocultar opciones.
 * La protección real de rutas se hace en los guards (PermissionRoute).
 */
export function ProtectedLayout() {
  const { permissions, hasPermission, hasAnyPermission } = useAuthStore();

  // Filtrar items del menú basándose en los permisos del usuario
  // Esto solo afecta la visualización del menú, no protege las rutas
  const menuItems = useMemo(() => {
    return filterMenuItemsByPermissions(
      allMenuItems,
      hasPermission,
      hasAnyPermission,
    );
  }, [permissions, hasPermission, hasAnyPermission]);

  return (
    <MainLayout menuItems={menuItems} title="Plataforma de Torneos">
      <Outlet />
    </MainLayout>
  );
}
